<template>
  <div 
    v-if="asyncDataStatusReady"
    class="container"
  >
    <el-form 
      :inline="true" 
      class="demo-form-inline"
    >
      <el-form-item label="Statuses">
        <el-select
          v-model="selectedStatuses"
          multiple
          placeholder="Select Statuses"
          collapse-tags
        >
          <el-option
            v-for="(status, index) in statuses"
            :key="index"
            :label="status"
            :value="status"
          />
        </el-select>
      </el-form-item>

      <el-form-item label="Categories">
        <el-select
          v-model="selectedCategories"
          multiple
          collapse-tags
          placeholder="Select Categories"
        >
          <el-option
            v-for="category in categories"
            :key="category.id"
            :label="category.name"
            :value="category.id"
          />
        </el-select>
      </el-form-item>

      <!-- <el-form-item label="Users">
        <el-select
          v-model="selectedUsers"
          multiple
          collapse-tags
          placeholder="Select Users"
        >
          <el-option
            v-for="user in users"
            :key="user.id"
            :label="user.firstName + ' ' + user.lastName"
            :value="user.id"
          />
        </el-select>
      </el-form-item> -->

      <el-form-item>
        <el-date-picker
          v-model="dateRanges"
          type="daterange"
          range-separator="To"
          start-placeholder="Start date"
          end-placeholder="End date"
          value-format="yyyy-MM-dd"
          format="yyyy-MM-dd"
        />
      </el-form-item>

      <el-form-item>
        <el-button
          type="primary"
          @click="searchTasks"
        >
          Search
        </el-button>
      </el-form-item>
    </el-form>
    <gantt-elastic
      :tasks="tasks" 
      :options="options" 
      :dynamic-style="dynamicStyle"
    />
  </div>
</template>

<script>
import GanttElastic from "../assets/gantt-elastic-gp";
import options from '../assets/options';
import dynamicStyle from '../assets/dynamicStyles';

import { mapGetters } from 'vuex';
import dayjs from 'dayjs';
import asyncDataStatus from '@/mixins/asyncDataStatus'
import NProgress from 'nprogress'

export default {
  name: 'Gantt',
  components: {
    GanttElastic,
  },

  mixins: [asyncDataStatus],

  data() {
    return {
      options,
      dynamicStyle,

      doNotshowCompletedTasks: false,
      selectedCategories: [],
      selectedStatuses: [],
      selectedUsers: [],
      dateRanges: []
    }
  },

  computed: {
    ...mapGetters({
      authStatus: 'auth/getAuthStatus',
      loadedTasks: 'tasks/getTasks',
      loadedStatuses: 'statuses/getStatuses',
      loadedCategories: 'categories/getCategories',
      loadedUsers: 'users/getUsers',
    }),

    previousWeek () {
      return this.today.subtract(1, 'week').format('YYYY-MM-DD')
    },

    today () {
      return dayjs()
    },

    nextWeek () {
      return this.today.add(2, 'week').format('YYYY-MM-DD')
    },

    tasks () {
      const tasks = this.loadedTasks.map((edge) => {
        const node = edge.node
        const task = {}
        
        task.id = node.id
        task.type = 'task'
        task.status = node.status.name
        task.category = node.category.name
        task.progress = 100
        task.style = {
          base: {
            fill: node.status.colour,
            stroke: node.status.colour,
          }
        }

        if (task.status === 'Awaiting Development') {
          task.style.base = { 
            ...task.style.base, 
            'stroke-dasharray': '5%', 
            'fill-opacity': 0.5, 
            'stroke': 'red',
            'stroke-width': 3
          }
        }

        if (node.assignedUser) {
          task.assignedUser = node.assignedUser.firstName + ' ' + node.assignedUser.lastName.slice(0, 1) + '.'
        } else {
          task.assignedUser = ''
        }

        task.label = `<a 
          href="https://clicksco.codebasehq.com/projects/${node.project.permalink}/tickets/${node.ticketId}/" 
          target="_blank" 
          rel="noopener noreferrer">${node.summary}</a>
        `

        if (node.dateRanges.length > 1) {
          task.tasks = node.dateRanges.map((date, index) => {
            let stage = { ...task } 
            
            stage.id = `${task.id}:${index + 1}`
            stage.start = date.start
            stage.end = dayjs(date.finish).add(1, 'day').format('YYYY-MM-DD')
            
            return stage
          })

          task.type = 'group'
        }

        task.start = node.dateRanges[node.dateRanges.length - 1].start
        task.end = dayjs(node.dateRanges[node.dateRanges.length - 1].finish).add(1, 'day').format('YYYY-MM-DD')

        return task
      })
      
      this.sortingByStatus(tasks)
      return tasks
    },

    statuses () {
      return Array.from(new Set(this.loadedStatuses.map((edge) => {
        return edge.node.name
      })))
    },

    categories () {
      return this.loadedCategories.map((edge) => {
        return { id: edge.node.id, name: edge.node.name }
      })
    },

    categoriesIds () {
      return this.categories.map((category) => {
        return category.id
      })
    },

    users () {
      return this.loadedUsers.map((edge) => {
        return edge.node
      })
    },
  },
  
  created () {
    if (this.authStatus) {
      this.dateRanges = [this.previousWeek, this.nextWeek]
      
      Promise.all([
        this.$store.dispatch('statuses/fetchStatuses'),
        this.$store.dispatch('categories/fetchCategories'),
        this.$store.dispatch('users/fetchUsers'),
      ])
      .then(() => {
        this.selectedCategories = [ ...this.categoriesIds ]
        this.selectedStatuses = [ ...this.statuses ]

        const dontShowStatuses = [
          'Invalid',
          'On Hold',
          'New',
          'Delivered',
          'Proposed'
        ]
        
        for (const status of dontShowStatuses) {
          const index = this.selectedStatuses.indexOf(status)
          if (index !== -1) this.selectedStatuses.splice(index, 1);
        }


        this.fetchTasks()
        .then(() => {
          this.asyncDataStatusFetched()
        })
        .catch(() => {
          this.$message.error('Oops, something happened.');
        })
      })
    }
  },

  methods: {
    searchTasks() {
      NProgress.start()
      this.fetchTasks()
      .then(() => {
        NProgress.done()
      })
    },

    fetchTasks () {
      return this.$store.dispatch('tasks/fetchTasks', { 
        dateStart: this.dateRanges[0], 
        dateFinish: this.dateRanges[1], 
        categoryIds: this.selectedCategories,
        statusNames: this.selectedStatuses,
        assignedUserIds: this.selectedUsers
      })
    },

    sortingByStatus (tasks) {
      tasks.sort((taskOne, taskTwo) => {
        if (taskOne.assignedUser > taskTwo.assignedUser) {
          return 1
        } else if (taskOne.assignedUser < taskTwo.assignedUser) {
          return -1
        } else if (taskOne.status > taskTwo.status) {
          return -1
        } else if (taskOne.status < taskTwo.status) {
          return 1
        } else {
          return 0
        }
      })
    
    }
  },

}
</script>

<style lang="scss" scopped>
  .container {
    margin: 10px;
  }

  .el-col {
    text-align: center;
  }

  .el-select > .el-select__tags {
    max-width: 210px !important;
  }

  .el-select .el-input .el-input__inner {
    cursor: pointer;
    padding-right: 40px;
  }
</style>>
